import type { Class } from '../../types/tournament';

import type { IMatchScheduleItem } from '../../types/schedule';

import { MatchScheduleInterval } from './scheduleConflictDetector';

import {
  buildDePhaseIndex,
  isDoubleElimClass,
  koIntervalsForPhaseIndex,
  schedulingOrderKey,
} from './scheduleDePhase';

import {
  areParallelMedalFinals,
  hasMedalFinalsSequentialViolation,
} from './scheduleMedalFinals';

import {
  hasGroupElimPhaseViolation,
  isBoxLeagueClass,
} from './scheduleConflictDetector';
import { boxRoundOrderingViolation } from './scheduleBoxLeagueRounds';

export function hasSchedulingOrderingViolation(
  a: MatchScheduleInterval,
  b: MatchScheduleInterval,
  classIdToObject: Record<string, Class>,
  dePhaseIndex: Map<number, number> | null,
  classIntervals?: MatchScheduleInterval[] | null
): boolean {
  if (a.match.classId !== b.match.classId) return false;

  const classObject =
    classIdToObject[String(a.match.classId)] ?? classIdToObject[a.match.classId];

  if (areParallelMedalFinals(a.match, b.match)) {
    return hasMedalFinalsSequentialViolation(a, b);
  }

  if (isBoxLeagueClass(classObject)) {
    return boxRoundOrderingViolation(a, b, classObject);
  }

  if (hasGroupElimPhaseViolation(a, b, classObject)) return true;

  if (a.match.tournamentGroup?.id && b.match.tournamentGroup?.id) return false;

  const intervals = classIntervals ?? [a, b];

  const aKey = schedulingOrderKey(a.match, classObject, dePhaseIndex, intervals);
  const bKey = schedulingOrderKey(b.match, classObject, dePhaseIndex, intervals);
  if (aKey == null || bKey == null) return false;
  if (aKey === bKey) return false;

  if (bKey < aKey && a.startMs < b.endMs) return true;
  if (bKey > aKey && b.startMs < a.endMs) return true;
  return false;
}

export function buildDePhaseIndexForClass(
  classIntervals: MatchScheduleInterval[],
  classObject: Class | undefined,
  unscheduledKoMatches: IMatchScheduleItem[] = []
): Map<number, number> | null {
  const merged = koIntervalsForPhaseIndex(classIntervals, unscheduledKoMatches);
  if (!isDoubleElimClass(classObject, merged)) return null;
  return buildDePhaseIndex(merged);
}

export function bracketOrderingShouldSkipPair(
  earlier: MatchScheduleInterval,
  later: MatchScheduleInterval
): boolean {
  if (!areParallelMedalFinals(earlier.match, later.match)) return false;
  return !hasMedalFinalsSequentialViolation(earlier, later);
}
